import { Button } from './ui/button';
import { useLanguage, type ActiveLanguage } from '../hooks/useLanguage';
import { useMarket, type MarketCode } from '../hooks/useMarketZustand';
import { Input } from './ui/input';
import { Separator } from './ui/separator';
import { logger } from '../utils/logger';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Mail,
  Phone,
  MapPin,
  Truck,
  Shield,
  Award,
  Globe,
} from 'lucide-react';

interface FooterProps {
  onPageNavigation?: (page: string) => void;
}

export function Footer({ onPageNavigation }: FooterProps) {
  const { t } = useTranslation('footer');
  const currentYear = new Date().getFullYear();
  const { currentLanguage, changeLanguage } = useLanguage();
  const { currentMarket, availableMarkets, changeMarket } = useMarket();

  // Simple market change handler
  const handleMarketChange = (newMarket: MarketCode) => {
    logger.log('🌍 Footer: Changing market to', newMarket);
    changeMarket(newMarket);
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Trust Indicators */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-3 gap-4 sm:gap-6 text-center">
            <div className="flex flex-col items-center">
              <Truck className="h-8 w-8 text-amber-400 mb-2" />
              <h4 className="font-medium text-white">
                {t('trustIndicators.freeShipping.title')}
              </h4>
              <p className="text-sm">
                {t('trustIndicators.freeShipping.description')}
              </p>
            </div>
            <div className="flex flex-col items-center">
              <Shield className="h-8 w-8 text-amber-400 mb-2" />
              <h4 className="font-medium text-white">
                {t('trustIndicators.securePayment.title')}
              </h4>
              <p className="text-sm">
                {t('trustIndicators.securePayment.description')}
              </p>
            </div>
            <div className="flex flex-col items-center">
              <Award className="h-8 w-8 text-amber-400 mb-2" />
              <h4 className="font-medium text-white">
                {t('trustIndicators.qualityGuarantee.title')}
              </h4>
              <p className="text-sm">
                {t('trustIndicators.qualityGuarantee.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div>
              <h2 className="text-2xl font-bold text-white">
                {t('company.name')}
              </h2>
              <p className="text-amber-400">{t('company.tagline')}</p>
            </div>
            <p className="text-sm leading-relaxed">
              {t('company.description')}
            </p>
            <div className="flex space-x-4">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-white"
              >
                <Facebook className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-white"
              >
                <Instagram className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-white"
              >
                <Twitter className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-white"
              >
                <Youtube className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              {t('quickLinks.title')}
            </h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => onPageNavigation?.('catalog')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('quickLinks.shopSpices')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation('blend-builder')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('quickLinks.blendBuilder')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation('subscription')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('quickLinks.subscription')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation?.('recipes')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('quickLinks.recipesAndBlog')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation?.('about')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('quickLinks.aboutUs')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation?.('contact')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('quickLinks.contact')}
                </button>
              </li>
            </ul>
          </div>

          {/* Customer Service */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              {t('customerService.title')}
            </h3>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => onPageNavigation?.('faq')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('customerService.faq')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation?.('shipping-returns')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('customerService.shippingReturns')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation?.('size-guide')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('customerService.sizeGuide')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation?.('track-order')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('customerService.trackOrder')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation?.('wholesale')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('customerService.wholesale')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => onPageNavigation?.('gift-cards')}
                  className="hover:text-white transition-colors text-left"
                >
                  {t('customerService.giftCards')}
                </button>
              </li>
            </ul>
          </div>

          {/* Newsletter & Contact */}
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-white mb-4">
                {t('newsletter.title')}
              </h3>
              <p className="text-sm mb-4">{t('newsletter.description')}</p>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder={t('newsletter.emailPlaceholder')}
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
                <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                  {t('newsletter.subscribe')}
                </Button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">
                {t('contact.title')}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-amber-400" />
                  <span>{t('contact.phone')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-amber-400" />
                  <span>{t('contact.email')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-amber-400" />
                  <span>{t('contact.address')}</span>
                </div>
              </div>

              {/* Language and Market Selectors under address */}
              <div className="flex items-center justify-center lg:justify-start gap-3 mt-4">
                {/* Language Selector */}
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-amber-400" />
                  <Select
                    value={currentLanguage}
                    onValueChange={(value: string) =>
                      changeLanguage(value as ActiveLanguage)
                    }
                  >
                    <SelectTrigger className="w-20 h-12 lg:h-6 bg-gray-800 border-gray-700 text-white text-sm lg:text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem
                        value="en"
                        className="text-white hover:bg-gray-700"
                      >
                        🇬🇧 EN
                      </SelectItem>
                      <SelectItem
                        value="de"
                        className="text-white hover:bg-gray-700"
                      >
                        🇩🇪 DE
                      </SelectItem>
                      <SelectItem
                        value="pl"
                        className="text-white hover:bg-gray-700"
                      >
                        🇵🇱 PL
                      </SelectItem>
                      <SelectItem
                        value="uk"
                        className="text-white hover:bg-gray-700"
                      >
                        🇺🇦 UK
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Location/Market Selector */}
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-amber-400" />
                  <Select
                    value={currentMarket}
                    onValueChange={(value: string) =>
                      handleMarketChange(value as MarketCode)
                    }
                  >
                    <SelectTrigger className="w-24 h-12 lg:h-6 bg-gray-800 border-gray-700 text-white text-sm lg:text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      {Object.entries(availableMarkets).map(
                        ([code, market]) => (
                          <SelectItem
                            key={code}
                            value={code}
                            className="text-white hover:bg-gray-700"
                          >
                            {market.flag} {market.currency}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator className="bg-gray-800" />

      {/* Bottom Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
          <div className="text-sm">
            {t('legal.copyright', { year: currentYear })}
          </div>

          {/* Center section with links */}
          <div className="flex flex-wrap gap-6 text-sm">
            <button
              onClick={() => onPageNavigation?.('privacy-policy')}
              className="hover:text-white transition-colors"
            >
              {t('legal.privacyPolicy')}
            </button>
            <button
              onClick={() => onPageNavigation?.('terms-of-service')}
              className="hover:text-white transition-colors"
            >
              {t('legal.termsOfService')}
            </button>
            <button
              onClick={() => onPageNavigation?.('cookie-policy')}
              className="hover:text-white transition-colors"
            >
              {t('legal.cookiePolicy')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
